import { Sparkles, Calendar, Building2, ArrowRight, CheckCircle2, Star, ExternalLink, Check, Plus } from 'lucide-react';
import DesignCard from './components/DesignCard';
import { designOptions, proposalMeta } from './data/designs';

function App() {
  const recommendedDesign = designOptions.find(d => d.status === 'recommended');

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#A0A0A0]">
      {/* Header with gezie.io branding */}
      <div className="border-b border-[rgba(255,255,255,0.07)]">
        <div className="container-proposal py-12">
          <div className="font-serif text-2xl text-[#F0F0F0] tracking-tight">
            gezie<span className="text-[#C75B2A]">.</span>io
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <header className="proposal-hero">
        <div className="container-proposal">
          <div className="text-center animate-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#141414] border border-[rgba(255,255,255,0.07)] px-4 py-2 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-[#C75B2A]" />
              <span className="text-sm font-medium text-[#A0A0A0]">
                Propuesta de Rediseño Web
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-[#F0F0F0] mb-6 leading-tight tracking-tight">
              Propuesta de
              <span className="block text-[#C75B2A]">
                Rediseño — Terres
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-[#A0A0A0] max-w-2xl mx-auto mb-8 leading-relaxed">
              Tres direcciones creativas para el sitio web de Terres. Cada opción ha sido 
              cuidadosamente diseñada para mejorar la experiencia de usuario y reflejar la 
              calidad premium de la marca.
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-[#666666] mb-12">
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                <span>Cliente: <strong className="text-[#A0A0A0]">{proposalMeta.client}</strong></span>
              </div>
              <span className="hidden sm:inline text-[#444444]">|</span>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{proposalMeta.date}</span>
              </div>
            </div>

            {/* Quick Jump to Recommended */}
            {recommendedDesign && (
              <a
                href={recommendedDesign.path}
                className="inline-flex items-center gap-2 bg-[#C75B2A] text-white px-8 py-4 rounded-lg font-medium transition-all duration-300 hover:bg-[#D46937] hover:shadow-lg hover:shadow-[rgba(199,91,42,0.3)]"
              >
                <Star className="w-5 h-5 fill-white" />
                Ver opción recomendada
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-proposal py-16">
        {/* Recommended Section */}
        {recommendedDesign && (
          <section className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[rgba(199,91,42,0.15)] rounded-lg flex items-center justify-center border border-[rgba(199,91,42,0.3)]">
                <Star className="w-5 h-5 text-[#C75B2A] fill-[#C75B2A]" />
              </div>
              <div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Opción Recomendada</h2>
                <p className="text-[#666666]">Nuestra recomendación principal basada en análisis de UX y performance</p>
              </div>
            </div>
            
            <div className="max-w-md mx-auto lg:max-w-none">
              <DesignCard design={recommendedDesign} index={0} />
            </div>

            {/* Property Detail Link - Only for app4 */}
            <div className="mt-6 p-6 bg-[#141414] rounded-lg border border-[rgba(199,91,42,0.3)]">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-medium text-[#F0F0F0] mb-1">Ver página de propiedad de ejemplo</h3>
                  <p className="text-sm text-[#666666]">Diseño completo de ficha de propiedad con galería, mapa y formulario de contacto.</p>
                </div>
                <a 
                  href="/app4/propiedad/1" 
                  className="inline-flex items-center gap-2 bg-[#C75B2A] text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:bg-[#D46937] whitespace-nowrap"
                >
                  Ver propiedad demo
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* All Options Section */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#1C1C1C] rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
              <Sparkles className="w-5 h-5 text-[#7A8B6E]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Todas las Opciones</h2>
              <p className="text-[#666666]">Compara las tres propuestas de diseño disponibles</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designOptions.map((design, index) => (
              <DesignCard key={design.id} design={design} index={index} />
            ))}
          </div>
        </section>

        {/* Alcance del Proyecto */}
        <section className="mt-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#1C1C1C] rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
              <CheckCircle2 className="w-5 h-5 text-[#C75B2A]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Alcance del Proyecto</h2>
              <p className="text-[#666666]">Todo lo que incluye el rediseño</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)]">
              <h3 className="text-xs font-semibold text-[#C75B2A] uppercase tracking-wider mb-4 pb-3 border-b border-[rgba(255,255,255,0.07)]">
                Sitio completo — Secciones core
              </h3>
              <ul className="space-y-3">
                {[
                  'Home con hero impactante y conversión',
                  'Cómo funciona (proceso paso a paso)',
                  'Beneficios y diferenciales',
                  'Propiedades / Oportunidades',
                  'Blog con artículos SEO optimizados',
                  'FAQs y recursos',
                  'Contacto con formularios + WhatsApp',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                    <ArrowRight className="w-4 h-4 text-[#C75B2A] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)]">
              <h3 className="text-xs font-semibold text-[#C75B2A] uppercase tracking-wider mb-4 pb-3 border-b border-[rgba(255,255,255,0.07)]">
                Incluido en todas las opciones
              </h3>
              <ul className="space-y-3">
                {[
                  'Diseño responsive (mobile, tablet, desktop)',
                  'CMS con autonomía editorial',
                  'Formularios segmentados por tipo',
                  'Integración WhatsApp',
                  'SEO técnico básico + Schema markup',
                  'Optimización Core Web Vitals',
                  'Código limpio y documentado',
                  'Capacitación CMS para el equipo',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                    <Check className="w-4 h-4 text-[#3ddc84] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Investment Section */}
        <section className="mt-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#1C1C1C] rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
              <Star className="w-5 h-5 text-[#C75B2A]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Inversión</h2>
              <p className="text-[#666666]">Opciones diseñadas para diferentes necesidades</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Tier 1 */}
            <div className="p-8 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)]">
              <div className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-2">Tier 1</div>
              <h3 className="font-serif text-xl font-medium text-[#F0F0F0] mb-4">Sitio Web Profesional</h3>
              <div className="text-4xl font-bold text-[#F0F0F0] mb-1">USD 4.500</div>
              <div className="text-sm text-[#666666] mb-6">Pago en 4 hitos</div>
              <ul className="space-y-3 mb-6">
                {[
                  'Todo lo incluido en el alcance base',
                  'Diseño responsive completo',
                  'SEO técnico básico',
                  'Soporte post-lanzamiento 30 días',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                    <Check className="w-4 h-4 text-[#C75B2A] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-[rgba(255,255,255,0.07)] text-sm text-[#666666]">
                <strong className="text-[#A0A0A0]">Entrega:</strong> 5–6 semanas
              </div>
            </div>

            {/* Tier 2 - Recommended */}
            <div className="p-8 bg-[rgba(199,91,42,0.08)] rounded-lg border border-[rgba(199,91,42,0.3)] relative">
              <div className="absolute -top-3 right-8 bg-[#C75B2A] text-white text-xs font-semibold px-3 py-1 rounded-full">
                Recomendado
              </div>
              <div className="text-xs font-semibold text-[#C75B2A] uppercase tracking-wider mb-2">Tier 2</div>
              <h3 className="font-serif text-xl font-medium text-[#F0F0F0] mb-4">Sitio Web + SEO Avanzado</h3>
              <div className="text-4xl font-bold text-[#C75B2A] mb-1">USD 7.500</div>
              <div className="text-sm text-[#666666] mb-6">Pago en 4 hitos</div>
              <ul className="space-y-3 mb-6">
                {[
                  'Todo lo de Tier 1, más:',
                  'Workshop de discovery y arquitectura',
                  'Link audit + basic SEO monitoring (3 meses)',
                  'Schema markup estructurado avanzado',
                  'Blog con sistema de categorías',
                  'Páginas de propiedades dinámicas',
                  'Soporte post-lanzamiento 60 días',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                    <Check className="w-4 h-4 text-[#C75B2A] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-[rgba(255,255,255,0.07)] text-sm text-[#666666]">
                <strong className="text-[#A0A0A0]">Entrega:</strong> 6–8 semanas
              </div>
            </div>
          </div>

          {/* Add-on Pack */}
          <div className="mt-6 p-6 bg-[rgba(122,139,110,0.08)] rounded-lg border border-[rgba(122,139,110,0.3)]">
            <div className="text-xs font-semibold text-[#7A8B6E] uppercase tracking-wider mb-3">Add-on</div>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
              <h3 className="font-serif text-lg font-medium text-[#F0F0F0]">Pack Contenido SEO</h3>
              <div className="text-2xl font-bold text-[#7A8B6E]">USD 1.200</div>
            </div>
            <ul className="grid md:grid-cols-2 gap-3">
              {[
                '8 artículos optimizados para posicionamiento',
                'Keyword research sector inmobiliario',
                'Calendario editorial 3 meses',
                'Publicación e indexación en el sitio',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#A0A0A0]">
                  <Plus className="w-4 h-4 text-[#7A8B6E] mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Hosting */}
          <div className="mt-6 p-8 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)]">
            <div className="grid md:grid-cols-[1fr_2fr] gap-8">
              {/* Free hosting - first 3 months */}
              <div className="p-6 bg-[#0A0A0A] rounded-lg border border-[rgba(255,255,255,0.07)]">
                <div className="text-xs font-semibold text-[#3ddc84] uppercase tracking-wider mb-3">Hosting incluido</div>
                <div className="text-4xl font-bold text-[#F0F0F0] mb-1">USD 0<span className="text-lg text-[#666666] ml-1">/mes</span></div>
                <p className="text-sm text-[#666666] mt-3">Primeros 3 meses sin costo — incluye SSL, CDN y soporte técnico.</p>
              </div>
              
              {/* Plans from month 4 */}
              <div className="p-6">
                <div className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-4">A partir del mes 4</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[rgba(255,255,255,0.07)]">
                    <div className="text-xl font-bold text-[#F0F0F0] mb-1">USD 30<span className="text-xs text-[#666666] ml-1">/mes</span></div>
                    <div className="text-sm font-semibold text-[#C75B2A] mb-2">Estándar</div>
                    <p className="text-xs text-[#666666]">Hosting, SSL, backups, soporte básico</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[rgba(255,255,255,0.07)]">
                    <div className="text-xl font-bold text-[#F0F0F0] mb-1">USD 60<span className="text-xs text-[#666666] ml-1">/mes</span></div>
                    <div className="text-sm font-semibold text-[#C75B2A] mb-2">Profesional</div>
                    <p className="text-xs text-[#666666]">+ CDN premium, staging, monitoreo 24/7</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[rgba(255,255,255,0.07)]">
                    <div className="text-xl font-bold text-[#F0F0F0] mb-1">USD 100<span className="text-xs text-[#666666] ml-1">/mes</span></div>
                    <div className="text-sm font-semibold text-[#C75B2A] mb-2">Enterprise</div>
                    <p className="text-xs text-[#666666]">+ SLA garantizado, soporte prioritario, actualizaciones de seguridad</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Terms */}
        <section className="mt-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#1C1C1C] rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
              <Calendar className="w-5 h-5 text-[#C75B2A]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Forma de Pago</h2>
              <p className="text-[#666666]">Cuatro hitos, cero sorpresas</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { percent: '40%', label: 'Al iniciar', desc: 'Confirmación del proyecto' },
              { percent: '30%', label: 'Al aprobar', desc: 'Diseño visual' },
              { percent: '20%', label: 'Al entregar', desc: 'En staging' },
              { percent: '10%', label: 'Al lanzar', desc: 'En producción' },
            ].map((step, i) => (
              <div key={i} className="p-6 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)] text-center">
                <div className="text-3xl font-bold text-[#C75B2A] mb-2">{step.percent}</div>
                <div className="text-sm font-medium text-[#F0F0F0] mb-1">{step.label}</div>
                <div className="text-xs text-[#666666]">{step.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Why gezie.io */}
        <section className="mt-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#1C1C1C] rounded-lg flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
              <Star className="w-5 h-5 text-[#C75B2A]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#F0F0F0]">Por qué gezie.io</h2>
              <p className="text-[#666666]">No somos una agencia genérica</p>
            </div>
          </div>

          <div className="space-y-4">
            {[
              'Especialistas en SEO y performance web — no solo diseñamos, posicionamos.',
              'Código propio, diseñado y programado desde cero — sin templates, sin page builders.',
              'Core Web Vitals en verde desde el primer prototipo — rendimiento real, no promesas.',
              'Stack moderno: React + TypeScript + Vite para máxima velocidad.',
              '3 prototipos funcionales para que elijas con certeza.',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#141414] rounded-lg border border-[rgba(255,255,255,0.07)]">
                <span className="text-[#C75B2A] font-bold">—</span>
                <span className="text-[#A0A0A0]">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-24 text-center py-16 border-t border-[rgba(255,255,255,0.07)]">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-[#F0F0F0] mb-6 italic">
            ¿Empezamos?
          </h2>
          <div className="flex flex-col items-center gap-3 mb-8">
            <a href="mailto:pda@gezie.io" className="text-[#C75B2A] hover:underline text-lg">
              pda@gezie.io
            </a>
            <a href="https://wa.me/12199511814" target="_blank" rel="noopener" className="text-[#C75B2A] hover:underline text-lg">
              +1 (219) 951-1814 — WhatsApp
            </a>
          </div>
          <p className="text-sm text-[#666666]">Esta propuesta es válida por 30 días desde su envío.</p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[rgba(255,255,255,0.07)] py-12">
        <div className="container-proposal">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <div className="font-sans font-semibold text-[#F0F0F0]">
                gezie<span className="text-[#C75B2A]">.</span>io
              </div>
              <p className="text-sm text-[#666666]">Propuesta de rediseño web — Terres</p>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a href="/app/" className="text-[#666666] hover:text-[#C75B2A] transition-colors">Opción A</a>
              <a href="/app2/" className="text-[#666666] hover:text-[#C75B2A] transition-colors">Opción B</a>
              <a href="/app4/" className="text-[#C75B2A] font-medium">Opción C (Recomendada)</a>
              <a href="/app4/propiedad/1" className="text-[#666666] hover:text-[#C75B2A] transition-colors">Propiedad Demo</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.07)] text-center text-xs text-[#444444]">
            <p>© {new Date().getFullYear()} gezie.io — Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
